import { useMemo } from "react";
import { documentId, where } from "firebase/firestore";
import { useUser } from "@/hooks/firestore/user/useUser";
import { useFirestore } from "@/hooks/firestore/core/useFirestore";
import {
  casinoGame,
  type CasinoGame,
} from "@/features/casino/models/casinoGame";
import { useCasinoCategories } from "@/features/casino/hooks/useCasinoCategories";
import {
  getGameCategories,
  processCasinoGame,
  sortByLastPlayed,
} from "@/features/casino/utils/helpers";

const getGamesInPlayOrder = (
  games: (CasinoGame & { id: string })[],
  gameIds: string[],
) => gameIds.flatMap((gameId) => games.find(({ id }) => id === gameId) ?? []);

/**
 * This user's recently played games, most recent first, filtered to games
 * that are still allowed in `country` and still belong to at least one
 * active category.
 */
const useRecentlyPlayedGames = (
  country?: string,
  { enabled = true }: { enabled?: boolean } = {},
) => {
  const { user, error: userError, isLoading: isLoadingUser } = useUser();
  const { categories, isLoading: isLoadingCategories } =
    useCasinoCategories(enabled);

  const playedGameIds = useMemo(
    () => sortByLastPlayed(user?.casinoRecentlyPlayed),
    [user],
  );

  const key = enabled && playedGameIds.length ? "/casinoGamesV2" : null;
  const {
    data,
    error: gamesError,
    isLoading: isLoadingGames,
  } = useFirestore(key, casinoGame.array(), {
    keyPath: "id",
    query: playedGameIds.length
      ? [where(documentId(), "in", [...playedGameIds].sort())]
      : undefined,
    getFromServer: true,
  });

  const recentlyPlayedGames = useMemo(() => {
    if (!data) return data;

    const ordered = getGamesInPlayOrder(data, playedGameIds);
    const allowed = country
      ? ordered.filter((game) => game.allowedCountries.includes(country))
      : ordered;
    // an empty list while loading would make every game look uncategorised
    const inActiveCategory = isLoadingCategories
      ? allowed
      : allowed.filter(
          (game) => getGameCategories(game.categories, categories).length > 0,
        );

    return inActiveCategory.map((game) => processCasinoGame(game, categories));
  }, [data, playedGameIds, country, categories, isLoadingCategories]);

  return {
    recentlyPlayedGames,
    recentlyPlayedError: userError ?? gamesError,
    isLoadingRecentlyPlayed:
      isLoadingUser || isLoadingCategories || isLoadingGames,
  } as const;
};

export { useRecentlyPlayedGames };
